import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import {
  collection, doc, getDoc, setDoc, addDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Button, Paper, TextField,
  Chip, IconButton, Alert, CircularProgress, Divider,
  Tabs, Tab, Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Preview';
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled';
import ImageUpload from '../components/ImageUpload';
import CVImport from '../components/CVImport';

const ALLOWED_ROLES = ['superadmin', 'admin', 'gruppledare', 'anbudsansvarig'];

const EMPTY_PROFILE = {
  personal: {
    name: '', title: '', company: '', location: '',
    email: '', phone: '', linkedin: '', photo: ''
  },
  ingress: '',
  competencies: [],
  tools: { expert: [], good: [] },
  standards: [],
  industries: [],
  assignments: [],
  education: [],
  visibleBlocks: [
    'competencies', 'assignments', 'education', 'tools', 'standards', 'industries'
  ]
};

export default function SubcontractorProfile() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { subId } = useParams();
  const isNew = subId === 'new';

  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [docId, setDocId] = useState(null);

  // chip input states
  const [newCompetency, setNewCompetency] = useState('');
  const [newStandard, setNewStandard] = useState('');
  const [newIndustry, setNewIndustry] = useState('');
  const [newToolExpert, setNewToolExpert] = useState('');
  const [newToolGood, setNewToolGood] = useState('');

  useEffect(() => {
    async function load() {
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const role = userSnap.data()?.role || '';
      if (!ALLOWED_ROLES.includes(role)) { setLoading(false); return; }
      setAuthorized(true);

      if (!isNew) {
        const snap = await getDoc(doc(db, 'subcontractors', subId));
        if (snap.exists()) {
          setProfile({ ...EMPTY_PROFILE, ...snap.data() });
          setDocId(snap.id);
        }
      }
      setLoading(false);
    }
    load();
  }, [currentUser, subId, isNew]);

  function setPersonal(field, value) {
    setProfile(p => ({ ...p, personal: { ...p.personal, [field]: value } }));
  }

  function addChip(field, value, setter) {
    if (!value.trim()) return;
    setProfile(p => ({ ...p, [field]: [...(p[field] || []), value.trim()] }));
    setter('');
  }

  function removeChip(field, index) {
    setProfile(p => ({ ...p, [field]: p[field].filter((_, i) => i !== index) }));
  }

  function addTool(level, value, setter) {
    if (!value.trim()) return;
    setProfile(p => ({
      ...p,
      tools: { ...p.tools, [level]: [...(p.tools?.[level] || []), value.trim()] }
    }));
    setter('');
  }

  function removeTool(level, index) {
    setProfile(p => ({
      ...p,
      tools: { ...p.tools, [level]: p.tools[level].filter((_, i) => i !== index) }
    }));
  }

  function addAssignment() {
    setProfile(p => ({
      ...p,
      assignments: [...p.assignments, {
        name: '', client: '', period: '', role: '',
        projectDescription: '', personalDescription: ''
      }]
    }));
  }

  function updateAssignment(index, field, value) {
    setProfile(p => ({
      ...p,
      assignments: p.assignments.map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  }

  function removeAssignment(index) {
    setProfile(p => ({
      ...p, assignments: p.assignments.filter((_, i) => i !== index)
    }));
  }

  function addEducation() {
    setProfile(p => ({
      ...p,
      education: [...p.education, { degree: '', school: '', year: '' }]
    }));
  }

  function updateEducation(index, field, value) {
    setProfile(p => ({
      ...p,
      education: p.education.map((e, i) => i === index ? { ...e, [field]: value } : e)
    }));
  }

  function removeEducation(index) {
    setProfile(p => ({
      ...p, education: p.education.filter((_, i) => i !== index)
    }));
  }

  async function handleSave() {
    setSaving(true);
    const data = {
      ...profile,
      type: 'subcontractor',
      updatedAt: new Date(),
      updatedBy: currentUser.uid
    };
    if (isNew) {
      const ref = await addDoc(collection(db, 'subcontractors'), {
        ...data, createdAt: new Date(), createdBy: currentUser.uid
      });
      setDocId(ref.id);
      navigate(`/subcontractors/${ref.id}`, { replace: true });
    } else {
      await setDoc(doc(db, 'subcontractors', docId || subId), data, { merge: true });
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!authorized) return (
    <Box sx={{ p: 4 }}>
      <Alert severity="error">Du har inte behörighet att se denna sida.</Alert>
    </Box>
  );

  const currentId = docId || (isNew ? null : subId);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Topbar */}
      <Box sx={{ backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LagbasLogo />
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
            <IconButton color="inherit" onClick={() => navigate('/subcontractors')}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonAddDisabledIcon />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {isNew ? 'Ny underkonsult' : (profile.personal?.name || 'Redigera underkonsult')}
                </Typography>
                <Chip label="Underkonsult" size="small"
                  sx={{ bgcolor: '#FF6B00', color: 'white', fontSize: 11, mt: 0.5 }} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {currentId && (
              <Button variant="outlined" startIcon={<PreviewIcon />}
                sx={{ borderColor: 'white', color: 'white' }}
                onClick={() => navigate(`/subcontractors/${currentId}/preview`)}>
                Förhandsgranska
              </Button>
            )}
            <Button variant="contained" startIcon={<SaveIcon />}
              sx={{ bgcolor: 'white', color: '#1a1a1a' }}
              onClick={handleSave} disabled={saving}>
              {saving ? 'Sparar...' : 'Spara'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3, pb: 6 }}>
        {saved && <Alert severity="success" sx={{ mb: 2 }}>Profilen sparad!</Alert>}

        <CVImport onImport={imported => {
          setProfile(prev => ({
            ...prev,
            ...imported,
            personal: {
              ...prev.personal,
              ...imported.personal,
              // Behåll befintligt bolagsnamn om det redan fyllts i
              company: prev.personal?.company || imported.personal?.company || ''
            },
            visibleBlocks: prev.visibleBlocks
          }));
          setActiveTab(0);
        }} />

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab label="Personuppgifter" />
          <Tab label="Kompetenser & verktyg" />
          <Tab label="Uppdrag" />
          <Tab label="Utbildning" />
        </Tabs>

        {/* TAB 0: Personuppgifter */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 3, mb: 3, alignItems: 'flex-start' }}>
              <Box>
                <Avatar src={profile.personal?.photo} sx={{ width: 80, height: 80, mb: 1 }}>
                  {profile.personal?.name?.charAt(0) || '?'}
                </Avatar>
                <ImageUpload
                  value={profile.personal?.photo}
                  onChange={url => setPersonal('photo', url)}
                  label="Foto"
                />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Namn" fullWidth value={profile.personal?.name || ''}
                  onChange={e => setPersonal('name', e.target.value)} />
                <TextField label="Titel / roll" fullWidth value={profile.personal?.title || ''}
                  onChange={e => setPersonal('title', e.target.value)} />
                <TextField label="Bolag (underkonsultens arbetsgivare)" fullWidth
                  value={profile.personal?.company || ''}
                  onChange={e => setPersonal('company', e.target.value)}
                  placeholder="t.ex. Konsult AB" />
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Ort" fullWidth value={profile.personal?.location || ''}
                onChange={e => setPersonal('location', e.target.value)} />
              <TextField label="E-post" fullWidth value={profile.personal?.email || ''}
                onChange={e => setPersonal('email', e.target.value)} />
              <TextField label="Telefon" fullWidth value={profile.personal?.phone || ''}
                onChange={e => setPersonal('phone', e.target.value)} />
              <TextField label="LinkedIn-adress" fullWidth value={profile.personal?.linkedin || ''}
                onChange={e => setPersonal('linkedin', e.target.value)}
                placeholder="https://www.linkedin.com/in/..." />
              <TextField label="Ingress / sammanfattning" fullWidth multiline rows={4}
                value={profile.ingress || ''}
                onChange={e => setProfile(p => ({ ...p, ingress: e.target.value }))} />
            </Box>
          </Paper>
        )}

        {/* TAB 1: Kompetenser & verktyg */}
        {activeTab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Kompetenser</Typography>
              <ChipInput
                value={newCompetency} onChange={setNewCompetency}
                onAdd={() => addChip('competencies', newCompetency, setNewCompetency)}
                placeholder="Lägg till kompetens"
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {profile.competencies?.map((c, i) => (
                  <Chip key={i} label={c} onDelete={() => removeChip('competencies', i)} />
                ))}
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Verktyg – expert</Typography>
              <ChipInput
                value={newToolExpert} onChange={setNewToolExpert}
                onAdd={() => addTool('expert', newToolExpert, setNewToolExpert)}
                placeholder="Lägg till verktyg"
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {profile.tools?.expert?.map((t, i) => (
                  <Chip key={i} label={t} onDelete={() => removeTool('expert', i)} />
                ))}
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Verktyg – god kännedom</Typography>
              <ChipInput
                value={newToolGood} onChange={setNewToolGood}
                onAdd={() => addTool('good', newToolGood, setNewToolGood)}
                placeholder="Lägg till verktyg"
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {profile.tools?.good?.map((t, i) => (
                  <Chip key={i} label={t} onDelete={() => removeTool('good', i)} />
                ))}
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Standarder & regelverk</Typography>
              <ChipInput
                value={newStandard} onChange={setNewStandard}
                onAdd={() => addChip('standards', newStandard, setNewStandard)}
                placeholder="Lägg till standard"
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {profile.standards?.map((s, i) => (
                  <Chip key={i} label={s} onDelete={() => removeChip('standards', i)} />
                ))}
              </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Branscher</Typography>
              <ChipInput
                value={newIndustry} onChange={setNewIndustry}
                onAdd={() => addChip('industries', newIndustry, setNewIndustry)}
                placeholder="Lägg till bransch"
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {profile.industries?.map((ind, i) => (
                  <Chip key={i} label={ind} onDelete={() => removeChip('industries', i)} />
                ))}
              </Box>
            </Paper>
          </Box>
        )}

        {/* TAB 2: Uppdrag */}
        {activeTab === 2 && (
          <Box>
            <Button variant="outlined" startIcon={<AddIcon />}
              onClick={addAssignment} sx={{ mb: 2 }}>
              Lägg till uppdrag
            </Button>
            {profile.assignments?.map((a, i) => (
              <Paper key={i} sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography fontWeight="bold">Uppdrag {i + 1}</Typography>
                  <IconButton size="small" color="error" onClick={() => removeAssignment(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label="Uppdragsnamn / projekt" fullWidth
                    value={a.name || ''} onChange={e => updateAssignment(i, 'name', e.target.value)} />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField label="Kund / beställare" fullWidth
                      value={a.client || ''} onChange={e => updateAssignment(i, 'client', e.target.value)} />
                    <TextField label="Period" fullWidth
                      value={a.period || ''} onChange={e => updateAssignment(i, 'period', e.target.value)}
                      placeholder="t.ex. 2022–2024" />
                  </Box>
                  <TextField label="Roll" fullWidth
                    value={a.role || ''} onChange={e => updateAssignment(i, 'role', e.target.value)} />
                  <TextField label="Projektbeskrivning" fullWidth multiline rows={2}
                    value={a.projectDescription || ''}
                    onChange={e => updateAssignment(i, 'projectDescription', e.target.value)} />
                  <TextField label="Personlig insats" fullWidth multiline rows={2}
                    value={a.personalDescription || ''}
                    onChange={e => updateAssignment(i, 'personalDescription', e.target.value)} />
                </Box>
              </Paper>
            ))}
            {profile.assignments?.length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Inga uppdrag tillagda ännu.</Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* TAB 3: Utbildning */}
        {activeTab === 3 && (
          <Box>
            <Button variant="outlined" startIcon={<AddIcon />}
              onClick={addEducation} sx={{ mb: 2 }}>
              Lägg till utbildning
            </Button>
            {profile.education?.map((e, i) => (
              <Paper key={i} sx={{ p: 3, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography fontWeight="bold">Utbildning {i + 1}</Typography>
                  <IconButton size="small" color="error" onClick={() => removeEducation(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField label="Examen / program" fullWidth
                    value={e.degree || ''} onChange={ev => updateEducation(i, 'degree', ev.target.value)} />
                  <TextField label="Lärosäte" fullWidth
                    value={e.school || ''} onChange={ev => updateEducation(i, 'school', ev.target.value)} />
                  <TextField label="År" fullWidth
                    value={e.year || ''} onChange={ev => updateEducation(i, 'year', ev.target.value)} />
                </Box>
              </Paper>
            ))}
            {profile.education?.length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Ingen utbildning tillagd ännu.</Typography>
              </Paper>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}

function ChipInput({ value, onChange, onAdd, placeholder }) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField size="small" fullWidth value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
      />
      <Button variant="outlined" onClick={onAdd} disabled={!value.trim()}>
        <AddIcon fontSize="small" />
      </Button>
    </Box>
  );
}
